import React, { Component } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}
/**
 * ErrorBoundary Component
 * Catches JavaScript errors and logs full details for debugging
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError(error: Error): State {
    console.error('[ErrorBoundary] ===== ERROR CAUGHT =====');
    console.error('[ErrorBoundary] Error:', error);
    console.error('[ErrorBoundary] Error name:', error.name);
    console.error('[ErrorBoundary] Error message:', error.message);
    console.error('[ErrorBoundary] Error stack:', error.stack);
    console.error('[ErrorBoundary] Error cause:', (error as any).cause);
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] ===== COMPONENT DID CATCH =====');
    console.error('[ErrorBoundary] Error:', error);
    console.error('[ErrorBoundary] Error Info:', errorInfo);
    console.error('[ErrorBoundary] Component Stack:', errorInfo.componentStack);
    // Log full error object
    console.error('[ErrorBoundary] Full error object:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: (error as any).cause,
      errorInfo: errorInfo,
      componentStack: errorInfo.componentStack
    });
    this.setState({
      errorInfo
    });
  }
  render() {
    if (this.state.hasError) {
      return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-164">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl w-full border-2 border-red-200" data-id="element-165">
            <div className="flex items-center gap-3 mb-4" data-id="element-166">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center" data-id="element-167">
                <AlertTriangleIcon className="w-6 h-6 text-red-600" data-id="element-168" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-169">
                React Error Caught
              </h1>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-170">
              The ErrorBoundary caught an error. Full details are logged to the
              console.
            </p>
            {/* Error Details */}
            {this.state.error && <div className="space-y-4 mb-6" data-id="element-171">
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4" data-id="element-172">
                  <div className="font-bold text-red-900 mb-2" data-id="element-173">Error Name:</div>
                  <p className="text-sm text-red-800 font-mono" data-id="element-174">
                    {this.state.error.name}
                  </p>
                </div>
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4" data-id="element-175">
                  <div className="font-bold text-red-900 mb-2" data-id="element-176">
                    Error Message:
                  </div>
                  <p className="text-sm text-red-800 font-mono" data-id="element-177">
                    {this.state.error.message}
                  </p>
                </div>
                {this.state.error.stack && <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto" data-id="element-178">
                    <div className="font-bold text-red-900 mb-2" data-id="element-179">
                      Error Stack:
                    </div>
                    <pre className="text-xs text-red-800 font-mono whitespace-pre-wrap" data-id="element-180">
                      {this.state.error.stack}
                    </pre>
                  </div>}
                {this.state.errorInfo?.componentStack && <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto" data-id="element-181">
                    <div className="font-bold text-red-900 mb-2" data-id="element-182">
                      Component Stack:
                    </div>
                    <pre className="text-xs text-red-800 font-mono whitespace-pre-wrap" data-id="element-183">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>}
              </div>}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6" data-id="element-184">
              <p className="text-sm text-blue-900" data-id="element-185">
                <strong data-id="element-186">Debug Instructions:</strong>
                <br data-id="element-187" />
                1. Open your browser console (F12)
                <br data-id="element-188" />
                2. Look for the detailed error logs starting with
                [ErrorBoundary]
                <br data-id="element-189" />
                3. The full error object, stack trace, and component stack are
                logged there
                <br data-id="element-190" />
                4. Common React error #310 causes: Invalid hook call, hooks
                called conditionally, multiple React copies
              </p>
            </div>
            <button onClick={() => {
            localStorage.removeItem('demo-authenticated');
            window.location.reload();
          }} className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold" data-id="element-191">
              Reset and Reload
            </button>
          </div>
        </div>;
    }
    return this.props.children;
  }
}